import React, { useEffect, useMemo, useState } from 'react'
import './style.scss';
import { ConversionCampaign, ConversionCampaignOffer, OfferMeta } from '../../../types/conversions';
import moment from 'moment';
import bonusIcon from '../../../assets/images/icons/bonus.png';
import silverCoinImg from "../../../assets/images/silvercoin-1.png"
import goldCoinImg from "../../../assets/images/goldcoin-1.png"
import influnsImg from "../../../assets/images/influns-icon-1.png"
import { APP_NAME } from '../../../constants/constants';
import { formatAmount } from '../../../utils/helpers';
import { useNavigate } from 'react-router-dom';



export const offerDetails: Record<'creator_commission' | 'platform_sale' | 'creator_sale', OfferMeta> = {
    "creator_commission": {
        type: 'creator_commission',
        title: "Commission Bonus Offer",
        icon: silverCoinImg,
        color_class: "success-light",
        unlocked_text: "Get upto extra <commission> on top of total commission earned.",
        sort_order: 1,
        style: 'silver',
        bonux_text: "Your Earned Commissions"
    },
    "platform_sale": {
        type: 'platform_sale',
        title: "PLATFORM MILESTONE BONUS",
        icon: influnsImg,
        color_class: "primary",
        unlocked_text: `Help ${APP_NAME} hit <sale_amount> in sales, unlock <commission> from the shared reward pool.`,
        sort_order: 3,
        style: 'influns',
        bonux_text: "Help us reach"
    },
    "creator_sale": {
        type: 'creator_sale',
        title: "Sales Bonus Offer",
        icon: goldCoinImg,
        color_class: "warning",
        unlocked_text: `Help ${APP_NAME} hit <sale_amount> in sales, unlock <commission> from the shared reward pool.`,
        sort_order: 3,
        style: 'gold',
        bonux_text: "Your Total Sales"
    },
}

const formatInr = (amount: number) => {
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
}

const safePercent = (value: number) => {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
}

const fillTemplate = (template: string, vars: Record<string, string>) => {
    return template
        .replace(/<commission>/g, vars.commission ?? '')
        .replace(/<sale_amount>/g, vars.sale_amount ?? '');
}

const MyProgress: React.FC<{ campaign: ConversionCampaign }> = ({ campaign }) => {

    const [selectedOffer, setSelectedOffer] = useState<ConversionCampaignOffer | null>(null);
    const navigate = useNavigate();
    const uniqueMonths: Record<string, { name: string; sort_order: number; month: string }> = useMemo(() => {
        if (!campaign?.offers) return {};
        const monthSet = new Set<string>();
        campaign.offers.forEach(offer => {
            if (offer.effective_month) monthSet.add(offer.effective_month);
        });
        const monthKeys = Array.from(monthSet)
            .sort((a, b) => b.localeCompare(a))
            .slice(0, 6);
        const result: Record<string, { name: string; sort_order: number; month: string }> = {};
        monthKeys.forEach((ym, idx) => {
            result[ym] = {
                name: moment(ym, "YYYY-MM").format("MMMM"),
                sort_order: idx + 1,
                month: ym
            };
        });
        return result;
    }, [campaign?.offers]);

    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

    const campaign_optin = campaign?.optin;

    const months = useMemo(() => {
        return Object.values(uniqueMonths).sort((a, b) => a.sort_order - b.sort_order);
    }, [uniqueMonths]);

    const offersForSelectedMonth = useMemo(() => {
        const offers = campaign?.offers ?? [];
        if (!selectedMonth) return [];
        return offers
            .filter((o) => o.effective_month === selectedMonth)
            .sort((a, b) => {
                const aOrder = offerDetails[a.type]?.sort_order ?? 999;
                const bOrder = offerDetails[b.type]?.sort_order ?? 999;
                return aOrder - bOrder;
            });
    }, [campaign?.offers, selectedMonth]);

    const offerHavingSalesOrCommission = useMemo(() => {
        return offersForSelectedMonth.find((o) => o.type === 'creator_commission' || o.type === 'creator_sale');
    }, [offersForSelectedMonth]);

    const totalSales = useMemo(() => {
        return (offerHavingSalesOrCommission?.optin?.total_sale_amount ?? 0);
    }, [offersForSelectedMonth]);

    const totalCommission = useMemo(() => {
        return (offerHavingSalesOrCommission?.optin?.total_commission_amount ?? 0);
    }, [offersForSelectedMonth]);

    const totalBonus = useMemo(() => {
        const completedMilestones = [];
        for (let offer of offersForSelectedMonth) {
            for (let milestone of offer.milestones) {
                if (Number(milestone.progress?.percent) >= 100) {
                    completedMilestones.push(milestone);
                }
            }
        }
        return completedMilestones.reduce((acc, milestone) => acc + Number(milestone.bonus_value.amount), 0);
    }, [offersForSelectedMonth]);

    useEffect(() => {
        if (Object.keys(uniqueMonths).length > 0) {
            const recentMonth = Object.keys(uniqueMonths).sort((a, b) => b.localeCompare(a))[0];
            setSelectedMonth(recentMonth);
        } else {
            setSelectedMonth(null);
        }
    }, [uniqueMonths]);

    return (
        <>
            <div className="my-progress py-3">
                {months.length > 0 && (
                    <div className="my-progress__months" role="tablist" aria-label="Months">
                        {months.map((m) => (
                            <button
                                key={m.month}
                                type="button"
                                className={`my-progress__month-chip ${selectedMonth === m.month ? 'is-active' : ''}`}
                                onClick={() => setSelectedMonth(m.month)}
                                role="tab"
                                aria-selected={selectedMonth === m.month}
                            >
                                {m.name}
                            </button>
                        ))}
                    </div>
                )}
                <div className="my-progress__performance_card app-card-wrapper mt-3">
                    {
                        
                        campaign_optin && campaign_optin.approved &&
                        <div className="app-card box-shadow-none  border-radius-1">
                            <div className="app-card-header">
                                <span className='text-dark fs-16 fw-500'>Your performance</span>
                            </div>

                            <div className="my-progress__performance-stats mt-2">
                                <div className="my-progress__performance-stat">
                                    <div className="my-progress__performance-value">{formatAmount(totalSales)}</div>
                                    <div className="my-progress__performance-label">Total Sales</div>
                                </div>

                                <div className="my-progress__performance-stat">
                                    <div className="my-progress__performance-value">{formatAmount(totalCommission)}</div>
                                    <div className="my-progress__performance-label">Commission Earned</div>
                                </div>
                            </div>
                        </div>
                    }
                    {
                        totalBonus > 0 && (
                            <div className="additional-info d-flex align-items-center gap-1">
                                <img src={bonusIcon} alt="Bonus" className='w-20px h-20px object-fit-cover' />
                                <span>
                                    You have earned a <b>{formatAmount(totalBonus)}</b> bonus this month
                                </span>
                            </div>
                        )
                    }
                </div>

                <div className="my-progress__offers mt-4">
                    {offersForSelectedMonth.map((offer) => {
                        const meta = offerDetails?.[offer.type as keyof typeof offerDetails];

                        const targetType = offer.visibility_target_type;
                        const targetAmount = offer.milestone_visible ? Number(offer.target) : Number(offer.visibility_target_amount);

                        const acheivedAmount = offer.milestone_visible ? (offer.type === 'creator_commission' ? Number(offer.optin?.total_commission_amount) : Number(offer.optin?.total_sale_amount)) : (targetType === 'sale' ? Number(offer.optin?.total_sale_amount) : Number(offer.optin?.total_commission_amount));

                        const percent = (acheivedAmount / targetAmount) * 100;

                        const title = offer.title;
                        const description = offer.description;

                        return (
                            <div className="my-progress__offer" key={offer.uuid}>
                                <div className="my-progress__offer-kicker">{title}</div>

                                {
                                    campaign_optin && campaign_optin.approved &&
                                    <div onClick={() => navigate(`/conversion/${campaign.uuid}/offer/${offer.uuid}`)} className="my-progress__offer-card position-relative justify-content-end">
                                        <img className={`my-progress__offer-icon ${meta?.style ?? ''} left-0`} src={meta?.icon ?? ''} alt="" />
                                        <div className="my-progress__offer-body">
                                            <div className="my-progress__offer-title">
                                                {description}
                                            </div>

                                            <div className="my-progress__offer-progress">
                                                <div className="my-progress__offer-bar">
                                                    <div
                                                        className={`my-progress__offer-bar-fill is-${meta?.color_class ?? ''}`}
                                                        style={{ width: `${percent}%` }}>
                                                    </div>
                                                </div>

                                                <div className="my-progress__offer-row">
                                                    <div className="my-progress__offer-amounts">
                                                        <span className={`my-progress__offer-current is-${meta.color_class}`}>{formatAmount(acheivedAmount)}</span>
                                                        <span className="my-progress__offer-of">OF {formatAmount(targetAmount)}</span>
                                                    </div>
                                                    <div className={`my-progress__offer-percent is-${meta.color_class}`}>{Math.round(percent)}%</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                                {
                                    (!campaign_optin || (campaign_optin && !campaign_optin.approved)) &&
                                    <div className="my-progress__offer-card position-relative justify-content-end">
                                        <img className={`my-progress__offer-icon ${meta?.style ?? ''} left-0`} src={meta?.icon ?? ''} alt="" />
                                        <div className="my-progress__offer-body">
                                            <div className="my-progress__offer-title">
                                                {description}
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        )
                    })}
                </div>
            </div>
        </>

    )
}

export default MyProgress